import React, { type FunctionComponent } from 'react'
import { useForm } from 'react-hook-form'
import { MerchandisingFormSection } from 'domains/merchandising/components/form-section/MerchandisingFormSection'
import { MerchandisingFormRow } from 'domains/merchandising/components/form-row/MerchandisingFormRow'
import { MerchandisingFormField } from 'domains/merchandising/components/form-field/MerchandisingFormField'
import { MerchandisingFormSelect } from 'domains/merchandising/components/form-input/MerchandisingFormSelect'
import { MerchandisingFormInput } from 'domains/merchandising/components/form-input/MerchandisingFormInput'
import { MerchandisingFormActions } from 'domains/merchandising/components/form-actions/MerchandisingFormActions'
import { useSelectOptionsDashMessage } from 'domains/banners/utils/intl.hooks'
import {
  type VisibilityConditionFormData,
  type VisibilityConditionFormProps,
} from 'domains/banners/types/visibilityCondition.types'
import {
  VisibilityConditionTypeEnum,
  visibilityConditionOptions,
  countryOptions,
  dayOfWeekOptions,
} from 'domains/banners/constants/visibilityCondition.constants'
import { PlacementPlatformEnum } from 'domains/public/api'

const emptyInitialState: VisibilityConditionFormData = {
  conditionType: null,
  conditionData: {
    countryIds: {
      countryIds: '',
    },
    dayOfWeek: {
      days: '',
    },
    expressMembership: {
      membershipStatus: false,
    },
    expressNonmemberLongTermHoldout: {},
    matchesAnyRetailerId: {
      retailerIds: '',
    },
    minimumPlatformVersion: {
      platform: PlacementPlatformEnum.Ios,
      version: null,
    },
    retailerAvailability: {
      retailerId: null,
    },
    shoppedAtRetailer: {
      retailerId: null,
      hasShopped: false,
    },
    shopped: {
      hasShopped: null,
      countryId: null,
      storeConfigurationId: null,
    },
    userInteractionLimit: {},
    whitelistedPostalCodes: {
      postalCodes: '',
    },
    whitelistedZones: {
      zones: '',
    },
  },
}

export const VisibilityConditionForm: FunctionComponent<VisibilityConditionFormProps> = ({
  initialState = emptyInitialState,
  submitError,
  isSubmitting,
  onCancel,
  onFormSubmit,
}) => {
  const form = useForm<VisibilityConditionFormData>({
    mode: 'onBlur',
    defaultValues: { ...initialState },
  })
  const { handleSubmit, register, watch, setValue, errors } = form
  const visibilityConditionType: string = watch('conditionType') || null

  const isCountryIds = visibilityConditionType === VisibilityConditionTypeEnum.COUNTRY_IDS
  const isDayOfWeek = visibilityConditionType === VisibilityConditionTypeEnum.DAY_OF_WEEK
  const isExpressMembership =
    visibilityConditionType === VisibilityConditionTypeEnum.EXPRESS_MEMBERSHIP
  const isExpressNonmemberLongTermHoldout =
    visibilityConditionType === VisibilityConditionTypeEnum.EXPRESS_NONMEMBER_LONG_TERM_HOLDOUT
  const isMatchesAnyRetailerId =
    visibilityConditionType === VisibilityConditionTypeEnum.MATCHES_ANY_RETAILER_ID
  const isMinimumPlatformVersion =
    visibilityConditionType === VisibilityConditionTypeEnum.MINIMUM_PLATFORM_VERSION
  const isRetailerAvailability =
    visibilityConditionType === VisibilityConditionTypeEnum.RETAILER_AVAILABILITY
  const isShoppedAtRetailer =
    visibilityConditionType === VisibilityConditionTypeEnum.SHOPPED_AT_RETAILER
  const isShopped = visibilityConditionType === VisibilityConditionTypeEnum.SHOPPED
  const isUserInteractionLimit =
    visibilityConditionType === VisibilityConditionTypeEnum.USER_INTERACTION_LIMIT
  const isWhitelistedPostalCodes =
    visibilityConditionType === VisibilityConditionTypeEnum.WHITELISTED_POSTAL_CODES
  const isWhitelistedZones =
    visibilityConditionType === VisibilityConditionTypeEnum.WHITELISTED_ZONES

  const visibilityConditionTypeOptions = useSelectOptionsDashMessage(visibilityConditionOptions)

  const onSubmit = (formData: VisibilityConditionFormData) => {
    onFormSubmit({ ...emptyInitialState, ...formData }, form)
  }
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <MerchandisingFormSection
        titleId="bannersDomain.details.visibility-condition.form.title"
        tooltipId="bannersDomain.details.visibility-condition.form.tooltip"
        renderAction
      >
        <MerchandisingFormRow>
          <MerchandisingFormField>
            <MerchandisingFormSelect
              labelId="bannersDomain.details.visibility-condition.form.input.visibilityConditionType.label"
              identifier="conditionType"
              form={form}
              onChange={() => setValue('conditionData', emptyInitialState.conditionData)}
              options={visibilityConditionTypeOptions}
            />
          </MerchandisingFormField>
        </MerchandisingFormRow>
        <MerchandisingFormRow>
          {isCountryIds && (
            <MerchandisingFormField fullWidth>
              <MerchandisingFormSelect
                form={form}
                identifier="conditionData.countryIds.countryIds"
                labelId="bannersDomain.details.visibility-condition.form.input.country-ids.country-ids"
                isMulti
                required
                options={countryOptions}
              />
            </MerchandisingFormField>
          )}
          {isDayOfWeek && (
            <MerchandisingFormField fullWidth>
              <MerchandisingFormSelect
                form={form}
                identifier="conditionData.dayOfWeek.days"
                labelId="bannersDomain.details.visibility-condition.form.input.day-of-week.day-of-week"
                isMulti
                required
                options={dayOfWeekOptions}
              />
            </MerchandisingFormField>
          )}
          {isExpressMembership && (
            <MerchandisingFormField fullWidth>
              <MerchandisingFormSelect
                form={form}
                identifier="conditionData.expressMembership.membershipStatus"
                labelId="bannersDomain.details.visibility-condition.form.input.express-membership.membership-status"
                options={[
                  { value: 'true', label: 'True' },
                  { value: 'false', label: 'False' },
                ]}
                onChange={({ value }) =>
                  setValue('conditionData.expressMembership.membershipStatus', value)
                }
              />
            </MerchandisingFormField>
          )}
          {isExpressNonmemberLongTermHoldout}
          {isMatchesAnyRetailerId && (
            <MerchandisingFormField fullWidth>
              <MerchandisingFormInput
                ref={register({ required: true })}
                identifier="conditionData.matchesAnyRetailerId.retailerIds"
                labelId="bannersDomain.details.visibility-condition.form.input.matches-any-retailer-id.retailer-ids"
                errors={errors}
              />
            </MerchandisingFormField>
          )}
          {isMinimumPlatformVersion && (
            <MerchandisingFormField fullWidth>
              <MerchandisingFormSelect
                form={form}
                identifier="conditionData.minimumPlatformVersion.platform"
                labelId="bannersDomain.details.visibility-condition.form.input.minimum-platform-version.platform"
                options={[
                  { value: PlacementPlatformEnum.Ios, label: 'iOS' },
                  { value: PlacementPlatformEnum.Android, label: 'Android' },
                ]}
                onChange={({ value }) =>
                  setValue('conditionData.minimumPlatformVersion.platform', value)
                }
              />
              <MerchandisingFormInput
                ref={register({ required: true })}
                identifier="conditionData.minimumPlatformVersion.version"
                labelId="bannersDomain.details.visibility-condition.form.input.minimum-platform-version.version"
                errors={errors}
              />
            </MerchandisingFormField>
          )}
          {isRetailerAvailability && (
            <MerchandisingFormField fullWidth>
              <MerchandisingFormInput
                ref={register({ required: true })}
                identifier="conditionData.retailerAvailability.retailerId"
                labelId="bannersDomain.details.visibility-condition.form.input.retailer-availability.retailer-id"
                errors={errors}
              />
            </MerchandisingFormField>
          )}
          {isShoppedAtRetailer && (
            <MerchandisingFormField fullWidth>
              <MerchandisingFormInput
                ref={register({ required: true })}
                identifier="conditionData.shoppedAtRetailer.retailerId"
                labelId="bannersDomain.details.visibility-condition.form.input.shopped-at-retailer.retailer-id"
                errors={errors}
              />
              <MerchandisingFormSelect
                form={form}
                identifier="conditionData.shoppedAtRetailer.hasShopped"
                labelId="bannersDomain.details.visibility-condition.form.input.shopped-at-retailer.has-shopped"
                options={[
                  { value: 'true', label: 'True' },
                  { value: 'false', label: 'False' },
                ]}
                onChange={({ value }) =>
                  setValue('conditionData.shoppedAtRetailer.hasShopped', value)
                }
              />
            </MerchandisingFormField>
          )}
          {isShopped && (
            <MerchandisingFormField fullWidth>
              <MerchandisingFormInput
                ref={register({ required: true })}
                identifier="conditionData.shopped.countryId"
                labelId="bannersDomain.details.visibility-condition.form.input.shopped.country-id"
                errors={errors}
              />
              <MerchandisingFormInput
                ref={register({ required: true })}
                identifier="conditionData.shopped.storeConfigurationId"
                labelId="bannersDomain.details.visibility-condition.form.input.shopped.store-configuration-id"
                errors={errors}
              />
              <MerchandisingFormSelect
                form={form}
                identifier="conditionData.shopped.hasShopped"
                labelId="bannersDomain.details.visibility-condition.form.input.shopped.has-shopped"
                options={[
                  { value: 'true', label: 'True' },
                  { value: 'false', label: 'False' },
                ]}
                onChange={({ value }) => setValue('conditionData.shopped.hasShopped', value)}
              />
            </MerchandisingFormField>
          )}
          {isUserInteractionLimit && (
            <MerchandisingFormField fullWidth>
              <MerchandisingFormInput
                ref={register({ required: false })}
                identifier="conditionData.userInteractionLimit.maxImpressionsCount"
                labelId="bannersDomain.details.visibility-condition.form.input.user-interaction-limit.max-impressions-count"
                errors={errors}
              />
              <MerchandisingFormInput
                ref={register({ required: false })}
                identifier="conditionData.userInteractionLimit.maxEngagementsCount"
                labelId="bannersDomain.details.visibility-condition.form.input.user-interaction-limit.max-engagements-count"
                errors={errors}
              />
              <MerchandisingFormInput
                ref={register({ required: false })}
                identifier="conditionData.userInteractionLimit.maxDismissedCount"
                labelId="bannersDomain.details.visibility-condition.form.input.user-interaction-limit.max-dismissed-count"
                errors={errors}
              />
              <MerchandisingFormInput
                ref={register({ required: false })}
                identifier="conditionData.userInteractionLimit.countCooldownSec"
                labelId="bannersDomain.details.visibility-condition.form.input.user-interaction-limit.count-cooldown-sec"
                errors={errors}
              />
            </MerchandisingFormField>
          )}
          {isWhitelistedPostalCodes && (
            <MerchandisingFormField fullWidth>
              <MerchandisingFormInput
                ref={register({ required: true })}
                identifier="conditionData.whitelistedPostalCodes.postalCodes"
                labelId="bannersDomain.details.visibility-condition.form.input.whitelisted-postal-codes.postal-codes"
                errors={errors}
              />
            </MerchandisingFormField>
          )}
          {isWhitelistedZones && (
            <MerchandisingFormField fullWidth>
              <MerchandisingFormInput
                ref={register({ required: true })}
                identifier="conditionData.whitelistedZones.zones"
                labelId="bannersDomain.details.visibility-condition.form.input.whitelisted-zones.zones"
                errors={errors}
              />
            </MerchandisingFormField>
          )}
        </MerchandisingFormRow>
        <MerchandisingFormRow>
          <MerchandisingFormActions
            onCancel={onCancel}
            isLoading={isSubmitting}
            submitError={submitError}
          />
        </MerchandisingFormRow>
        <MerchandisingFormRow>
          {submitError && <div>{submitError.message}</div>}
        </MerchandisingFormRow>
      </MerchandisingFormSection>
    </form>
  )
}
